import { useState } from "react";
import { ArrowUpRight } from "lucide-react";

const STUDIES = [
  {
    client: "Replique",
    sector: "Industrial 3D Printing / Digital Manufacturing",
    tag: "GTM Strategy",
    challenge: "Creating a new category — 'digital warehousing' — that didn't exist in procurement budgets, requiring education across Supply Chain, Engineering, and Sustainability stakeholders.",
    solution: [
      "Market sizing across spare-parts, after-sales, and obsolescence-driven verticals",
      "ICP definition prioritising OEMs with long product lifecycles",
      "Hybrid route-to-market (direct + partner ecosystem + OEM marketplaces)",
      "Land-and-expand playbook with measurable milestones",
    ],
    outcomes: [
      { k: "Focused GTM", v: "Accelerated entry into automotive after-sales" },
      { k: "Partner net", v: "Defensible decentralised delivery" },
      { k: "Replicable", v: "Sales motion across priority verticals" },
    ],
  },
  {
    client: "Outokumpu",
    sector: "Sustainable Stainless Steel",
    tag: "Commercial Positioning",
    challenge: "Converting industry-leading low-carbon footprint into a commercial premium without losing volume customers anchored to traditional buying criteria.",
    solution: [
      "Differentiated GTM plays for automotive OEMs, construction and process industries",
      "Value-based pricing framework linking carbon premium to ESG outcomes",
      "Channel optimisation across mills, service centres, and distributors",
      "Sales enablement — selling sustainability as a P&L lever",
    ],
    outcomes: [
      { k: "Repositioned", v: "Sustainability from message to commercial argument" },
      { k: "Pricing", v: "Premium logic adopted across priority segments" },
      { k: "Conversations", v: "Stronger with ESG-driven enterprise accounts" },
    ],
  },
  {
    client: "Brose Fahrzeugteile",
    sector: "Automotive Mechatronics Supplier",
    tag: "EV Transformation",
    challenge: "The shift to EVs, software-defined vehicles and new entrants disrupted traditional Tier-1 relationships. Brose needed share in new-energy OEMs while protecting European base.",
    solution: [
      "White-space analysis across EV-native OEMs and SDV platforms",
      "Account tiering and coverage model for emerging Chinese and US EV programs",
      "Product-line GTM strategies for door modules, seat systems, and e-motors",
      "Co-development framework with Tier-0.5 integrators and software platforms",
    ],
    outcomes: [
      { k: "Coverage", v: "Structured commercial view of high-growth EV programs" },
      { k: "Strategy", v: "Clear product-line plans vs. one-size accounts" },
      { k: "Partnership", v: "Stronger posture with software / platform players" },
    ],
  },
  {
    client: "Unispares",
    sector: "Precision Machined Components / Tractor & Automotive Parts",
    tag: "Market Insights",
    challenge: "Multi-geography operations without unified view of customer behaviour, competitor pricing, or aftermarket demand — decisions were instinct-led.",
    solution: [
      "Market & customer research across UK, EU, and Indian aftermarket channels",
      "Product and pricing analysis benchmarking OEM-equivalents and grey-market",
      "Buyer journey mapping for fleet operators, distributors, and workshop networks",
      "Competitive intelligence dashboard for ongoing decision support",
    ],
    outcomes: [
      { k: "One truth", v: "Where to play and how to win" },
      { k: "Roadmap", v: "Prioritised & aligned to validated demand" },
      { k: "Pricing", v: "Defensible architecture across geographies" },
    ],
  },
  {
    client: "SITEC Production",
    sector: "Precision Manufacturing / E-Mobility Production Lines",
    tag: "Segmentation & VOC",
    challenge: "Two business units targeted overlapping but distinct buyers — duplicated outreach, missed cross-sell signals, and under-invested in battery and e-drive segments.",
    solution: [
      "Segmentation refresh distinguishing component from capital-equipment buyers",
      "Voice-of-customer across Tier-1 and Tier-2 e-mobility accounts",
      "Win/loss analysis on recent capital equipment bids",
      "Demand-signal mapping linking EV programs to capacity-investment cycles",
    ],
    outcomes: [
      { k: "Aligned", v: "Both BUs on shared priority accounts" },
      { k: "Proactive", v: "Commercial motion (vs. RFP-driven)" },
      { k: "Targeting", v: "Sharper on the most lucrative EV programs" },
    ],
  },
  {
    client: "Comate Engineering & Design",
    sector: "Product Design & Engineering Consultancy",
    tag: "ABM",
    challenge: "Pipeline dominated by inbound referrals — growth unpredictable. Ideal clients (innovation directors) were unreachable through generic tactics.",
    solution: [
      "~150 high-fit innovation-led manufacturers across Benelux and DACH",
      "Persona-led ABM playbooks for R&D Directors, Innovation Managers, Founders",
      "Multi-channel orchestration across LinkedIn, exec events, case-study nurture",
      "Sales-marketing alignment with shared SLAs and account scorecards",
    ],
    outcomes: [
      { k: "Predictable", v: "Named-account pipeline complementing inbound" },
      { k: "Focus", v: "On highest-value innovation programs" },
      { k: "Alignment", v: "Stronger sales-marketing on shared accounts" },
    ],
  },
  {
    client: "Svekon",
    sector: "Multidisciplinary Engineering Services",
    tag: "Defence ABM",
    challenge: "Strong reputation in Swedish defence and big-science circles but unknown to adjacent primes, EU defence programs, and dual-use technology companies.",
    solution: [
      "Tiered TAL across Swedish primes, Nordic integrators, and EU dual-use OEMs",
      "Capability dossiers tailored by program (radar, C4ISR, space, energy)",
      "Executive ABM plays combining 1:1 outreach with industry events",
      "Pipeline governance aligned to long defence procurement cycles",
    ],
    outcomes: [
      { k: "Coverage", v: "Structured across priority defence and dual-use accounts" },
      { k: "Credibility", v: "Translated technical into proactive outreach" },
      { k: "Visibility", v: "Across multi-year procurement cycles" },
    ],
  },
  {
    client: "Jernbro",
    sector: "Industrial Services & Maintenance",
    tag: "Media & Campaign",
    challenge: "Shift perception from 'maintenance vendor' to 'strategic reliability partner' consistently across Scandinavia — different regional priorities and media habits.",
    solution: [
      "Integrated campaign — Scandinavia-wide narrative with local activation layers",
      "Media planning across LinkedIn, Nordic trade press, programmatic, and events",
      "Always-on + burst model balancing brand-building with lead-gen peaks",
      "Performance reporting with single-pane attribution across paid, owned, earned",
    ],
    outcomes: [
      { k: "Coordinated", v: "Media presence across Scandinavia" },
      { k: "SOV lift", v: "Within industrial trade media" },
      { k: "Inbound", v: "Measurable flow from plant-level decision-makers" },
    ],
  },
  {
    client: "SBS (formerly Sopra Banking Software)",
    sector: "Banking & Financial Software",
    tag: "Rebrand & Demand",
    challenge: "Post-rebrand from Sopra Banking Software — re-establish recognition under SBS identity while running demand across multiple regions, product lines and buyer types.",
    solution: [
      "Rebrand-aware campaign framework preserving equity while reinforcing SBS",
      "Cross-product plan with distinct narratives for core banking, lending, digital",
      "Regional activation tailored to EMEA, APAC, and the Americas",
      "Closed-loop measurement linking spend to MQL, SQL, and pipeline by product",
    ],
    outcomes: [
      { k: "Re-anchoring", v: "Successful brand recognition under SBS" },
      { k: "Pipeline", v: "Consistent qualified across regions and product lines" },
      { k: "Visibility", v: "Clear ROI per product and per market" },
    ],
  },
  {
    client: "Proximus NXT Luxembourg",
    sector: "ICT & Telecommunications",
    tag: "Creative Platform",
    challenge: "Broad portfolio experienced by enterprise buyers as a list of products rather than a coherent digital-transformation partnership. Content was scattered and inconsistent.",
    solution: [
      "Narrative architecture anchored on 'digital transformation, end to end'",
      "Creative platform unifying tone, visual identity, and message hierarchy",
      "Audience-led content pillars for CIO, CISO, Public Sector, Mid-Market",
      "Always-on editorial engine producing PoV papers, case studies, and assets",
    ],
    outcomes: [
      { k: "Reframed", v: "From product vendor to transformation partner" },
      { k: "Coherent", v: "Content system across audiences and channels" },
      { k: "Engagement", v: "Higher on executive-level content" },
    ],
  },
  {
    client: "Silverfin",
    sector: "Cloud Accounting & Compliance Software",
    tag: "Thought Leadership",
    challenge: "Long, trust-driven sale to accountancy firms. Silverfin needed content credible to managing partners and practitioners while differentiating from incumbents and AI entrants.",
    solution: [
      "Persona content for Managing Partners (firm ROI) and Senior Accountants (workflow)",
      "Creative platform positioning Silverfin as trustworthy AI for accounting firms",
      "Benchmark reports, AI-in-accounting POVs, and customer stories",
      "Localisation across UK, Benelux, France, and Luxembourg markets",
    ],
    outcomes: [
      { k: "Credibility", v: "With economic buyers and end-users" },
      { k: "Cycles", v: "Shortened evaluation in priority accounts" },
      { k: "Brand", v: "Consistent experience across European markets" },
    ],
  },
  {
    client: "Diconium",
    sector: "Digital Business Transformation / Tech Services",
    tag: "Brand Refresh",
    challenge: "Positioning evolved from 'digital agency' to 'intelligence built into everything' — required complete refresh of creative platform, content, and proof points.",
    solution: [
      "Brand platform translating 'Intelligence. Built into everything.' across touchpoints",
      "Content strategy on three pillars: AI & data, commerce, engineering at scale",
      "Hero content and case-study program featuring flagship enterprise work",
      "Sales enablement ensuring creative consistency through bottom-of-funnel",
    ],
    outcomes: [
      { k: "Coherent", v: "New positioning expressed across all touchpoints" },
      { k: "Credibility", v: "Enterprise proof strengthened by hero content" },
      { k: "Consistency", v: "From brand campaigns into sales conversations" },
    ],
  },
];

export default function CaseStudies() {
  const [active, setActive] = useState(0);
  const s = STUDIES[active];
  return (
    <section id="case-studies" data-testid="case-studies-section" className="relative py-24 md:py-32 border-t border-white/5 bg-[#040810]">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Case Studies · Real campaigns, real outcomes
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em]">
            Twelve programs. <span className="italic font-normal text-teal">Twelve stories.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            From category creation to multi-region rebrands — a snapshot of what
            our teams and platforms have delivered across European B2B.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: index */}
          <div className="lg:col-span-4 space-y-1 max-h-[720px] overflow-auto no-scrollbar">
            {STUDIES.map((st, i) => (
              <button
                key={st.client}
                data-testid={`case-study-tab-${i}`}
                onClick={() => setActive(i)}
                className={`w-full text-left rounded-xl border px-5 py-4 transition ${
                  active === i
                    ? "border-teal/40 bg-teal/[0.06]"
                    : "border-white/5 bg-[#0A121A] hover:border-white/15"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-teal mb-1">
                      {String(i + 1).padStart(2, "0")} · {st.tag}
                    </div>
                    <div className="font-display text-base font-semibold leading-tight">
                      {st.client}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{st.sector}</div>
                  </div>
                  <ArrowUpRight
                    size={14}
                    className={active === i ? "text-teal" : "text-muted-foreground"}
                  />
                </div>
              </button>
            ))}
          </div>

          {/* Right: detail */}
          <div className="lg:col-span-8 rounded-2xl border border-white/[0.06] bg-[#0A121A] p-8 md:p-10">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-teal mb-4">
              {s.sector}
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-semibold leading-tight tracking-tight">
              {s.client}
            </h3>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  The challenge
                </div>
                <p className="text-sm leading-relaxed text-foreground/90">{s.challenge}</p>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  Solution delivered
                </div>
                <ul className="space-y-2">
                  {s.solution.map((it) => (
                    <li key={it} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-teal mt-1.5 h-1 w-1 rounded-full shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10 border-t border-white/5 pt-8">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-teal mb-4">
                Outcomes
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {s.outcomes.map((o) => (
                  <div key={o.k} className="rounded-xl border border-white/5 p-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-teal mb-2">
                      {o.k}
                    </div>
                    <div className="text-sm leading-snug">{o.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
